import type { AlertColor } from '@mui/material'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useArticleInlineImages } from './useArticleInlineImages'
import { useArticleMetadata } from './useArticleMetadata'
import { useArticleResources } from './useArticleResources'
import { useArticleSave } from './useArticleSave'
import { useArticleUploads } from './useArticleUploads'
import { articleApi } from '../services/articleApi'
import type { ArticleState, TroubleshootingRow } from '../types/article.types'
import type { ArticleFormState } from '../types/article-form.types'
import {
  buildAutoTags,
  createEmptyForm,
  EMPTY_TROUBLESHOOTING_ROW,
  mapArticleToForm,
  normalizeTag,
} from '../utils/articleFormUtils'
import type { SnackbarState } from '../../../shared/types/common'

export function useArticleForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = Boolean(id)

  const [form, setForm] = useState<ArticleFormState>(createEmptyForm())
  const [tagInput, setTagInput] = useState('')
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' })
  const [loading, setLoading] = useState(false)
  const [articleStatus, setArticleStatus] = useState<ArticleState>('draft')

  const {
    setUploadStatuses,
    uploadInlineImages,
    uploadRegularAttachments,
    uploadStatuses,
  } = useArticleUploads()
  const resources = useArticleResources(showSnackbar)
  const metadata = useArticleMetadata(showSnackbar)
  const inlineImages = useArticleInlineImages({
    articleId: id,
    existingAttachments: resources.existingAttachments,
    form,
    images: resources.images,
    setForm,
    setImages: resources.setImages,
    setUploadStatuses,
  })

  const isCustomerSpecific = form.scope === 'customer-specific'
  const autoTags = buildAutoTags(form)
  const {
    errors,
    recoveryArticleId,
    saveDraft,
    saving,
    submitForReview,
    submitting,
  } = useArticleSave({
    clearPendingInlineImages: inlineImages.clearPendingInlineImages,
    clearPendingResources: resources.clearPendingResources,
    files: resources.files,
    form,
    id,
    images: resources.images,
    isCustomerSpecific,
    isEditing,
    navigateHome: () => navigate('/'),
    pendingDeletedAttachmentIds: resources.pendingDeletedAttachmentIds,
    pendingInlineImages: inlineImages.pendingInlineImages,
    setArticleStatus,
    setForm,
    showSnackbar,
    uploadInlineImages,
    uploadRegularAttachments,
  })

  useEffect(() => {
    setForm(current => {
      if (current.scope === 'customer-specific') {
        if (current.customer === 'general-customer') {
          return { ...current, customer: '' }
        }
        return current
      }

      if (current.scope && current.customer !== 'general-customer') {
        return { ...current, customer: 'general-customer' }
      }

      return current
    })
  }, [form.scope])

  useEffect(() => {
    if (!id) {
      return
    }

    void loadArticle(id)
  }, [id])

  async function loadArticle(articleId: string) {
    setLoading(true)
    try {
      const response = await articleApi.get(articleId)
      const article = response.data
      if (!['draft', 'changes_requested'].includes(article.state)) {
        showSnackbar('Only draft or changes-requested technotes can be edited', 'error')
        navigate(`/articles/${articleId}`)
        return
      }

      setArticleStatus(article.state)
      resources.setLoadedAttachments(article.attachments || [])
      setForm(mapArticleToForm(article))
    } catch {
      showSnackbar('Failed to load technote', 'error')
    } finally {
      setLoading(false)
    }
  }

  function setField<K extends keyof ArticleFormState>(field: K) {
    return (value: ArticleFormState[K]) => setForm(current => ({ ...current, [field]: value }))
  }

  function addTag(tag: string) {
    const trimmedTag = tag.trim()
    if (!trimmedTag) {
      return
    }

    const normalizedTag = normalizeTag(trimmedTag)
    if (!form.tags.some(currentTag => normalizeTag(currentTag) === normalizedTag)) {
      setForm(current => ({ ...current, tags: [...current.tags, trimmedTag] }))
    }

    setTagInput('')
  }

  function removeTag(tag: string) {
    setForm(current => ({ ...current, tags: current.tags.filter(currentTag => currentTag !== tag) }))
  }

  function addTroubleshootingRow() {
    setForm(current => ({
      ...current,
      troubleshooting_notes: [...current.troubleshooting_notes, { ...EMPTY_TROUBLESHOOTING_ROW }],
    }))
  }

  function updateTroubleshootingRow(index: number, field: keyof TroubleshootingRow, value: string) {
    setForm(current => {
      const nextRows = [...current.troubleshooting_notes]
      nextRows[index] = { ...nextRows[index], [field]: value }
      return { ...current, troubleshooting_notes: nextRows }
    })
  }

  function removeTroubleshootingRow(index: number) {
    setForm(current => ({
      ...current,
      troubleshooting_notes: current.troubleshooting_notes.filter((_, currentIndex) => currentIndex !== index),
    }))
  }

  function showSnackbar(message: string, severity: AlertColor = 'success') {
    setSnackbar({ open: true, message, severity })
  }

  return {
    autoTags,
    currentUserRole: metadata.currentUserRole,
    dropdownOptions: metadata.dropdownOptions,
    errors,
    files: resources.files,
    form,
    handleInlineImagePaste: inlineImages.handleInlineImagePaste,
    handleRequestOption: metadata.handleRequestOption,
    images: resources.images,
    inlineLinkedImageLabels: inlineImages.inlineLinkedImageLabels,
    uploadStatuses,
    articleStatus,
    deleteExistingAttachment: resources.deleteExistingAttachment,
    existingAttachments: resources.existingAttachments,
    existingInlineImageLabels: inlineImages.existingInlineImageLabels,
    isCustomerSpecific,
    isEditing,
    hasRecoveryDraft: Boolean(recoveryArticleId),
    loading,
    openRequestDialog: metadata.openRequestDialog,
    renameLinkedInlineImage: inlineImages.renameLinkedInlineImage,
    removeTag,
    requestDialog: metadata.requestDialog,
    requestForm: metadata.requestForm,
    saveDraft,
    saving,
    setField,
    setFiles: resources.setFiles,
    setForm,
    setImages: resources.setImages,
    setRichTextField: inlineImages.setRichTextField,
    setRequestDialog: metadata.setRequestDialog,
    setRequestForm: metadata.setRequestForm,
    setSnackbar,
    setTagInput,
    snackbar,
    submitForReview,
    submitting,
    tagInput,
    workflowUsers: metadata.workflowUsers,
    addTag,
    addTroubleshootingRow,
    updateTroubleshootingRow,
    removeTroubleshootingRow,
  }
}
